/**
 * The provenance sheet — G2.4 and G2 test e.
 *
 *   "Every XLSX carries a first sheet stating the perimeter … The evo rule 'state
 *    the perimeter of analysis' is enforced by the file format, not by discipline."
 *
 * That last clause is the design instruction. This module exists so that stating
 * the perimeter is not something a screen can forget: `writeXlsx` cannot produce a
 * workbook without calling `provenanceRows`, and `assertComplete` throws on a
 * missing field rather than emitting a blank cell.
 */

import { APP_VERSION, COMMIT_SHA } from '../lib/build-info';

import { PROVENANCE_FIELDS, isAbsent, type Provenance, type ProvenanceAbsent, type Stated } from './types';

export class IncompleteProvenanceError extends Error {
  constructor(public readonly missing: readonly string[]) {
    super(
      `Provenance sheet is incomplete — missing ${missing.join(', ')}. `
      + 'G2 test e requires all nine fields populated. An export whose perimeter is '
      + 'unstated is a number without a claim attached to it, which is precisely '
      + 'what the provenance sheet exists to prevent.',
    );
    this.name = 'IncompleteProvenanceError';
  }
}

/** G2 test e — all nine fields, populated. Empty collections count as missing. */
export function assertComplete(p: Provenance): void {
  const missing = missingProvenanceFields(p);
  if (missing.length) throw new IncompleteProvenanceError(missing);
}

/** The fields `assertComplete` would refuse, in `PROVENANCE_FIELDS` order; empty when complete. */
export function missingProvenanceFields(p: Provenance): readonly string[] {
  const missing: string[] = [];
  for (const field of PROVENANCE_FIELDS) {
    const value = p[field];
    if (value === undefined || value === null) { missing.push(field); continue; }
    // A declared absence is a statement — but only with its reason (`docs/adr/9007`).
    if (isAbsent(value)) {
      if (value.reasonKey.trim() === '') missing.push(field);
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') { missing.push(field); continue; }
    if (Array.isArray(value) && value.length === 0 && field === 'measureDefinitions') {
      // An export with no cited measure definition cannot be re-derived, which
      // defeats G9's re-derivation test. `filters` may legitimately be empty — "no
      // filter applied" is itself a statement of perimeter — but a measure list
      // cannot be.
      missing.push(field);
    }
  }
  // `?.`: a field missing outright was already counted above; this checks the halves of a present one.
  if (!isAbsent(p.timeWindow) && (!p.timeWindow?.from || !p.timeWindow?.to)) missing.push('timeWindow');
  if (!isAbsent(p.generatedBy) && (!p.generatedBy?.principalId || !p.generatedBy?.displayName)) missing.push('generatedBy');
  return [...new Set(missing)];
}

/** The platform does not publish this field for the read the export was taken from. */
export const NOT_PUBLISHED: ProvenanceAbsent = { absent: true, reasonKey: 'export.provenance.absent.notPublished' };
/** The browser holds no verified identity; the server's export audit records who exported. */
export const NO_PRINCIPAL: ProvenanceAbsent = { absent: true, reasonKey: 'export.provenance.absent.noPrincipal' };
/** The screen does not yet record the perimeter it read, so no filter list can be claimed. */
export const PERIMETER_NOT_RECORDED: ProvenanceAbsent = {
  absent: true,
  reasonKey: 'export.provenance.absent.perimeterNotRecorded',
};

/**
 * The build that produced the file — this front end's release version and commit, which is
 * what the export actually came from. It replaced a `v0.1.0` constant in every chapter, a
 * version no release ever carried.
 */
export function buildVersion(): string {
  return `bata-ui ${APP_VERSION} (${COMMIT_SHA.slice(0, 12)})`;
}

export interface ProvenanceRow {
  /** ICU key for the field label — the sheet is localised like everything else. */
  readonly labelKey: string;
  /** The value, already a string. Raw and unformatted: an ISO timestamp stays ISO. */
  readonly value: string;
  /**
   * `value` is an ICU KEY, to be translated by the writer — a declared absence's reason, "no
   * filters", an illustrative note. Without this flag the writer printed the key itself into
   * the sheet (`export.provenance.noFilters`), which the note below always promised it would not.
   */
  readonly valueIsKey?: true;
}

/** A stated value as a sheet row: the value itself, or the absence's reason key. */
function stated<T>(labelKey: string, value: Stated<T>, render: (present: T) => string): ProvenanceRow {
  return isAbsent(value)
    ? { labelKey, value: value.reasonKey, valueIsKey: true }
    : { labelKey, value: render(value) };
}

/**
 * The nine fields as sheet rows, in the order G2.4 lists them. Order is stable
 * because the provenance test asserts against it and because a planner comparing
 * two exports reads down the same column.
 */
export function provenanceRows(p: Provenance): readonly ProvenanceRow[] {
  assertComplete(p);
  return renderRows(p, new Set());
}

/** Says a field the export did not state, in place of a blank or an invented value. */
const NOT_STATED_KEY = 'export.provenance.absent.notStated';

/**
 * The provenance rows of a file that must be produced whatever its builder stated — the Excel
 * workbook's provenance sheet (DEC-98, ADR 9010 as amended 2026-10-01).
 *
 * The workbook is the CSV's table plus this sheet, and it must never fail where the CSV of the
 * same table succeeds: the CSV carries no provenance block, so it never ran `assertComplete`,
 * and several builders written in the CSV-only weeks cite no measure. A field the builder did
 * not state is therefore written as NOT STATED — a declared absence with its reason, as ADR 9007
 * requires — never blank and never invented. `provenanceRows` keeps refusing for the callers that
 * need a complete set.
 */
export function fileProvenanceRows(p: Provenance): readonly ProvenanceRow[] {
  return renderRows(p, new Set(missingProvenanceFields(p)));
}

function renderRows(p: Provenance, missing: ReadonlySet<string>): readonly ProvenanceRow[] {
  const notStated = (labelKey: string): ProvenanceRow => ({ labelKey, value: NOT_STATED_KEY, valueIsKey: true });
  const or = (field: string, labelKey: string, row: () => ProvenanceRow): ProvenanceRow => (
    missing.has(field) ? notStated(labelKey) : row()
  );
  return [
    or('filters', 'export.provenance.filters', () => (isAbsent(p.filters)
      ? stated('export.provenance.filters', p.filters, () => '')
      : p.filters.length === 0
        ? { labelKey: 'export.provenance.filters', value: 'export.provenance.noFilters', valueIsKey: true }
        : { labelKey: 'export.provenance.filters', value: p.filters.map((f) => `${f.field} ${f.operator} ${f.value}`).join(' · ') })),
    or('hierarchyNode', 'export.provenance.hierarchyNode', () => stated('export.provenance.hierarchyNode', p.hierarchyNode, (node) => node)),
    or('timeWindow', 'export.provenance.timeWindow', () => stated('export.provenance.timeWindow', p.timeWindow, (window) => `${window.from}/${window.to}`)),
    or('scenario', 'export.provenance.scenario', () => stated('export.provenance.scenario', p.scenario, (scenario) => scenario)),
    or('planVersionId', 'export.provenance.planVersion', () => stated('export.provenance.planVersion', p.planVersionId, (plan) => plan)),
    or('measureDefinitions', 'export.provenance.measureDefinitions', () => ({
      labelKey: 'export.provenance.measureDefinitions',
      value: p.measureDefinitions
        .map((m) => `${m.measure} [${m.registerVersion}]: ${m.definition}`)
        .join(' · '),
    })),
    or('generatedAt', 'export.provenance.generatedAt', () => ({ labelKey: 'export.provenance.generatedAt', value: p.generatedAt })),
    or('generatedBy', 'export.provenance.generatedBy', () => stated('export.provenance.generatedBy', p.generatedBy, (who) => `${who.displayName} <${who.principalId}>`)),
    or('platformVersion', 'export.provenance.platformVersion', () => stated('export.provenance.platformVersion', p.platformVersion, (version) => version)),
    // Last, and only when there is something to say. See `Provenance.illustrative`: the
    // screen's marker is the one thing an export strips, so it is restated beside the
    // figures it is about.
    //
    // The value is an ICU KEY, translated downstream exactly as `noFilters` above is. A
    // sentence assembled here would ship untranslated into the one artefact that leaves the
    // product, which is the opposite of what this row is for.
    ...(p.illustrative ?? []).flatMap((i) => (
      i.columns === 'all'
        ? [{ labelKey: 'export.provenance.illustrative', value: i.reasonKey, valueIsKey: true as const }]
        : [
          { labelKey: 'export.provenance.illustrativeColumns', value: i.columns.join(', ') },
          { labelKey: 'export.provenance.illustrative', value: i.reasonKey, valueIsKey: true as const },
        ]
    )),
  ];
}
